import { Grid, Paper, Popover, Tooltip } from "@mui/material";
import React, { FC, ReactNode, useState } from "react";
import styles from "@/components/subjectOverview/subjectOverviewAnchorDataCard.module.css";
import { AnchorField, FieldKind } from "@/components/anchorData/anchorDataForm";
import { useTranslation } from "react-i18next";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import EditIcon from "@mui/icons-material/Edit";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useNavigate } from "react-router-dom";
import SubjectOverviewAnchorDataCardModal from "@/components/subjectOverview/subjectOverviewAnchorDataCardModal";
import { t } from "i18next";

interface SubjectOverviewAnchorDataCardProps {
  handleError: (value: any) => void;
  formValues?: BackendValues;
  subjectId?: string;
  isCompany?: boolean;
  handleNotificationMessage: (value: any) => void;
  handleNotificationVariant: (value: string) => void;
  handleShowNotification: (state: boolean) => void;
  onSuccessfullSubmit: (subjectId: any) => void;
}

type BackendFieldData = {
  value: unknown;
  label: string;
  editable: boolean;
  type: FieldKind;
  allowed_values_set?: string[];
};

const CUSTOMER_CARD_FIELDS = [
  "customer_id",
  "company_name",
  "firmensitz_strasse_und_hausnummer",
  "firmensitz_postleitzahl",
  "firmensitz_ort",
  "vertriebsstatus_arbeitssicherheit_gb",
  "vertriebsstatus_betriebsmedizin_gb",
  "kontakt_operativ_arbeitssicherheit",
  "kontakt_operativ_betriebsmedizin",
  "zustaendige_cs_mitarbeiter",
  "mitarbeiteranzahl_gesamt",
  "grundbetreuung_std_arbeitssicherheit",
  "zusaetzlich_vereinbarte_bplus_stunden_arbeitssicherheit",
  "grundbetreuung_std_betriebsmedizin",
  "zusaetzlich_vereinbarte_bplus_stunden_betriebsmedizin",
  "zustaendiger_arzt",
  "zustaendige_sifa",
  "anteil_allgemeine_betreuungszeit_prozent",
] as const;

type ContactData = {
  first_name?: string;
  last_name?: string;
  salutation?: string;
  position?: string;
  email?: string;
  phone_number?: string;
  [key: string]: unknown;
};

type BackendValues = Partial<Record<CustomerCardFieldName, BackendFieldData>>;
type CustomerCardFieldName = (typeof CUSTOMER_CARD_FIELDS)[number];

const SubjectOverviewAnchorDataCard: FC<SubjectOverviewAnchorDataCardProps> = ({
  formValues,
  subjectId,
  isCompany,
  handleError,
  handleNotificationMessage,
  handleNotificationVariant,
  handleShowNotification,
  onSuccessfullSubmit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const fields = React.useMemo(
    () => buildAnchorFields(formValues),
    [formValues]
  );

  const displayFields = React.useMemo(() => {
    const byName = new Map(fields.map((f) => [f.name, f]));

    const outgoingFields: AnchorField[] = [];

    for (const name of CUSTOMER_CARD_FIELDS) {
      if (name === "company_name") continue;

      if (name === "firmensitz_strasse_und_hausnummer") {
        const street = byName.get("firmensitz_strasse_und_hausnummer")?.value;
        const zip = byName.get("firmensitz_postleitzahl")?.value;
        const city = byName.get("firmensitz_ort")?.value;

        outgoingFields.push({
          name: "address",
          label: t("address") || "Adresse",
          type: "address",
          value: {
            street,
            zip,
            city,
          },
          editable: false,
        });

        continue;
      }

      if (name === "firmensitz_postleitzahl" || name === "firmensitz_ort") {
        continue;
      }

      const field = byName.get(name);
      if (field) outgoingFields.push(field);
    }

    return outgoingFields;
  }, [fields, t]);

  const navigateToBuildings = () => {
    if (subjectId) {
      navigate(`/buildings/${subjectId}`);
    }
  };

  const handleModalOpen = (modalState: boolean) => {
    setOpenEditModal(modalState);
  };

  return (
    <>
      <Paper className={styles.paperContainer}>
        <div className={styles.headerRow}>
          <h5 className={styles.heading}>{t("overview")}</h5>

          <div className={styles.headerActions}>
            {isCompany && (
              <Tooltip
                title={t("view_all_buildings_for_company")}
                placement="left"
              >
                <GenericButton variant="icon" onClick={navigateToBuildings}>
                  <MapsHomeWorkIcon />
                </GenericButton>
              </Tooltip>
            )}
            <Tooltip title={t("edit_anchordata")} placement="top">
              <GenericButton
                variant="icon"
                onClick={() => handleModalOpen(true)}
              >
                <EditIcon />
              </GenericButton>
            </Tooltip>
          </div>
        </div>

        <Grid container spacing={1.25}>
          {displayFields.map((field) => (
            <Grid key={field.name} item xs={12} sm={6} md={3}>
              <div className={styles.fieldItem}>
                <div className={styles.fieldLabel} title={field.label}>
                  {field.label}
                </div>

                <div className={styles.fieldValue}>
                  {field.type === "object_list" ? (
                    <ObjectListValue value={field.value} />
                  ) : (
                    formatByType(field)
                  )}
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <SubjectOverviewAnchorDataCardModal
        open={openEditModal}
        formFields={fields}
        onClose={handleModalOpen}
        handleError={handleError}
        handleNotificationMessage={handleNotificationMessage}
        handleNotificationVariant={handleNotificationVariant}
        handleShowNotification={handleShowNotification}
        subjectId={subjectId}
        onSuccessfullSubmit={onSuccessfullSubmit}
      />
    </>
  );
};

export default SubjectOverviewAnchorDataCard;

function isContactData(v: unknown): v is ContactData {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function buildAnchorFields(values?: BackendValues): AnchorField[] {
  return CUSTOMER_CARD_FIELDS.map((name) => {
    const data = values?.[name];
    const typeMapping = {
      email: "email",
      first_name: "str",
      last_name: "str",
      phone_number: "phone_number",
      position: "str",
      salutation: "str",
    };

    const labelOptions: any = [
      {
        value: "salutation",
        label: "Anrede",
      },
      {
        value: "first_name",
        label: "Vorname",
      },
      {
        value: "last_name",
        label: "Nachname",
      },
      {
        value: "email",
        label: "E-Mail",
      },
      {
        value: "position",
        label: "Stellenbezeichnung",
      },
      {
        value: "phone_number",
        label: "Telefonummer",
      },
    ];

    return {
      name,
      label: data?.label ?? name,
      type: data?.type ?? "str",
      value: data?.value ?? null,
      editable: data?.editable,
      options:
        data?.type === "object_list" ? labelOptions : data?.allowed_values_set,
      typeMapping: data?.type === "object_list" ? typeMapping : {},
      allowed_values_set: data?.allowed_values_set,
    };
  });
}

function formatContactName(c: ContactData): string {
  const first = typeof c.first_name === "string" ? c.first_name.trim() : "";
  const last = typeof c.last_name === "string" ? c.last_name.trim() : "";
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return full;

  const pos = typeof c.position === "string" ? c.position.trim() : "";
  if (pos) return pos;

  const email = typeof c.email === "string" ? c.email.trim() : "";
  if (email) return email;

  return "-";
}

function ObjectListValue({ value }: { value: unknown }) {
  const arr = Array.isArray(value) ? value : [];
  const contacts = arr.filter(isContactData) as ContactData[];

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  if (contacts.length === 0) return <span>—</span>;

  const first = contacts[0];
  const displayName = formatContactName(first);
  const extraCount = contacts.length - 1;

  const open = Boolean(anchorEl);
  const id = open ? "contact-popover" : undefined;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };
  return (
    <>
      <GenericButton
        ref={triggerRef}
        type="button"
        variant="outlined"
        className={styles.objectListTrigger}
        onClick={handleOpen}
        aria-describedby={id}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{ padding: "0" }}
      >
        <span>{displayName}</span>
        {extraCount > 0 && (
          <span className={styles.extraHint}>
            {t("additional_values_invisible_count", { count: extraCount })}{" "}
          </span>
        )}
      </GenericButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            className: styles.popoverPaper,
            onClick: (e) => e.stopPropagation(),
          },
        }}
      >
        <div className={styles.popoverContent}>
          {contacts.map((c, idx) => (
            <div key={idx} className={styles.popoverSection}>
              {contacts.length > 1 && (
                <div className={styles.popoverSectionTitle}>
                  Eintrag {idx + 1}
                </div>
              )}
              <ContactDetails c={c} />
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}

function formatFloatValue(value: number): string {
  const factor = 100;
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return String(rounded);
}

function formatByType(field: any): string | ReactNode {
  if (field.value === null || field.value === undefined) return "—";

  if (field.type === "float") {
    const num =
      typeof field.value === "number" ? field.value : Number(field.value);
    if (!Number.isFinite(num)) return "—";
    return formatFloatValue(num);
  }

  if (field.type === "percent") {
    const num =
      typeof field.value === "number" ? field.value : Number(field.value);
    if (!Number.isFinite(num)) return "—";
    return `${num.toFixed(2)} %`;
  }

  if (field.type === "enum") {
    if (!Array.isArray(field.allowed_values_set)) return "—";

    const match = field.allowed_values_set.find(
      (opt: any) => opt.value === field.value
    );

    return match?.label || "—";
  }

  if (field.type === "subject_id_list") {
    const arr = Array.isArray(field.value) ? field.value : [];

    const names = arr
      .filter((v: any): v is { name?: unknown } => !!v && typeof v === "object")
      .map((obj: any) => (typeof obj.name === "string" ? obj.name.trim() : ""))
      .filter(Boolean);

    return names.length ? names.join(", ") : "—";
  }

  if (field.type === "address") {
    const v = field.value as {
      street?: unknown;
      zip?: unknown;
      city?: unknown;
    };

    const street = typeof v.street === "string" ? v.street.trim() : "";
    const zip = v.zip ? String(v.zip).trim() : "";
    const city = typeof v.city === "string" ? v.city.trim() : "";

    if (!street && !zip && !city) return "—";

    return (
      <div className={styles.addressValue}>
        {street && <div>{street}</div>}
        {(zip || city) && <div>{[zip, city].filter(Boolean).join(" ")}</div>}
      </div>
    );
  }

  if (typeof field.value === "string") return field.value.trim() || "—";
  if (typeof field.value === "number") return String(field.value);

  return String(field.value);
}

function renderLinkOrDash(kind: "email" | "phone", value: unknown) {
  const v = typeof value === "string" ? value.trim() : "";
  if (!v) return "—";

  if (kind === "email") {
    return (
      <a
        className={styles.popoverAnchor}
        href={`mailto:${v}`}
        onClick={(e) => e.stopPropagation()}
      >
        {v}
      </a>
    );
  }

  const tel = v.replace(/\s+/g, "");
  return (
    <a
      className={styles.popoverAnchor}
      href={`tel:${tel}`}
      onClick={(e) => e.stopPropagation()}
    >
      {v}
    </a>
  );
}

function ContactDetails({ c }: { c: ContactData }) {
  const getText = (v: unknown) =>
    v === null || v === undefined
      ? "—"
      : typeof v === "string"
      ? v.trim() || "—"
      : String(v);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>Anrede:</span>
        <span>{getText(c.salutation)}</span>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>Vorname:</span>
        <span>{getText(c.first_name)}</span>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>Nachname:</span>
        <span>{getText(c.last_name)}</span>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>Position:</span>
        <span>{getText(c.position)}</span>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>E-Mail:</span>
        <span>{renderLinkOrDash("email", c.email)}</span>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}
      >
        <span style={{ opacity: 0.8 }}>Telefon:</span>
        <span>{renderLinkOrDash("phone", c.phone_number)}</span>
      </div>
    </div>
  );
}
